import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireAuth } from "@/lib/auth/middleware";
import * as db from "@/lib/db.server";
import { computeClaimRisk, RISK_FLAG_THRESHOLD } from "./claim-risk.server";
import { notifyIndexNow, providerUrl } from "@/lib/indexnow.server";
import { sendEmail, emailTemplates } from "./email.server";

/**
 * Auto-approve a provider claim. Computes silent risk score; never blocks the
 * provider. If the score crosses RISK_FLAG_THRESHOLD, the claim is marked
 * 'flagged' internally and an admin alert + notification is emitted.
 */
export const submitClaim = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .inputValidator(
    z.object({
      providerId: z.string().uuid(),
      plan: z.enum(["free", "premium_yearly", "premium_monthly", "lifetime_premium"]).default("free"),
      userPhone: z.string().max(40).optional(),
      userFullName: z.string().max(200).optional(),
    })
  )
  .handler(async ({ data, context }) => {
    const { userId, user } = context;
    const email = (user?.email as string | undefined) ?? null;

    // Load provider (read on public providers is fine; BYPASSRLS service pool).
    const provider = await db.one(
      `SELECT id, name, practice, specialty, city, state, website, npi, email, phone, claimed_by, claim_status
         FROM public.providers
        WHERE id = $1`,
      [data.providerId],
    );
    if (!provider) throw new Error("Listing not found");

    if (provider.claimed_by && provider.claimed_by !== userId) {
      throw new Error("This listing has already been claimed by another user.");
    }

    // Re-use counters
    const [userClaims24Row, phoneReuseRow, emailReuseRow] = await Promise.all([
      db.one<{ count: number }>(
        `SELECT count(*)::int AS count
           FROM public.providers
          WHERE claimed_by = $1
            AND claimed_at >= $2`,
        [userId, new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()],
      ),
      data.userPhone
        ? db.one<{ count: number }>(
            `SELECT count(*)::int AS count
               FROM public.providers
              WHERE phone = $1
                AND claimed_by IS NOT NULL`,
            [data.userPhone],
          )
        : Promise.resolve<{ count: number } | null>({ count: 0 }),
      email
        ? db.one<{ count: number }>(
            `SELECT count(*)::int AS count
               FROM public.providers
              WHERE email = $1
                AND claimed_by IS NOT NULL`,
            [email],
          )
        : Promise.resolve<{ count: number } | null>({ count: 0 }),
    ]);

    const risk = computeClaimRisk({
      userEmail: email,
      userPhone: data.userPhone ?? null,
      provider,
      sameUserClaimsLast24h: userClaims24Row?.count ?? 0,
      samePhoneClaimedCount: phoneReuseRow?.count ?? 0,
      sameEmailClaimedCount: emailReuseRow?.count ?? 0,
    });

    const flagged = risk.score >= RISK_FLAG_THRESHOLD;

    // Update provider — auto-approve no matter what. Owner-scoped: the row is
    // either unclaimed or already this user's (guarded above), so also bind
    // userId to keep the write from touching another user's claimed listing.
    await db.execute(
      `UPDATE public.providers
         SET claimed_by = $2,
             claimed_at = now(),
             claim_status = $3,
             auto_approved_at = now(),
             risk_score = $4,
             risk_flags = $5,
             listing_status = $6
       WHERE id = $1
         AND (claimed_by IS NULL OR claimed_by = $2)`,
      [
        data.providerId,
        userId,
        flagged ? "flagged" : "auto_approved",
        risk.score,
        risk.flags,
        data.plan === "free" ? "claimed" : "premium",
      ],
    );

    // Ensure the user has the doctor role
    await db.execute(
      `INSERT INTO public.user_roles (user_id, role)
       VALUES ($1, 'doctor')
       ON CONFLICT (user_id, role) DO NOTHING`,
      [userId],
    );

    // Insert claims record so existing approval triggers / emails still fire
    await db.execute(
      `INSERT INTO public.claims (provider_id, user_id, contact_email, status)
       VALUES ($1, $2, $3, 'approved')`,
      [data.providerId, userId, email],
    );

    if (flagged) {
      await db.execute(
        `INSERT INTO public.admin_claim_alerts
           (provider_id, user_id, risk_score, risk_flags, selected_plan, payment_status)
         VALUES ($1, $2, $3, $4, $5, $6)`,
        [
          data.providerId,
          userId,
          risk.score,
          risk.flags,
          data.plan,
          data.plan === "free" ? "n/a" : "pending",
        ],
      );
    }

    // Confirmation email to the doctor.
    if (email) {
      sendEmail({ to: email, ...emailTemplates.claimReceived(provider.practice || provider.name || "your listing") })
        .catch((e) => console.error("[claim] confirmation email failed:", e.message));
    }

    // Provider page just became live/claimed — ping IndexNow.
    notifyIndexNow(providerUrl(data.providerId));

    // Enroll free claims in the Provider Upgrade email sequence (sequence
    // stays in test_mode until an admin enables it, so no real emails go out
    // automatically).
    if (data.plan === "free") {
      try {
        const { enrollProviderInUpgradeSequenceInternal } = await import("./email-sequences.functions");
        await enrollProviderInUpgradeSequenceInternal(data.providerId);
      } catch (e) {
        console.error("upgrade-sequence enroll failed", e);
      }
    }


    return {
      providerId: data.providerId,
      plan: data.plan,
      claimStatus: flagged ? "flagged" : "auto_approved",
      needsCheckout: data.plan !== "free",
    };
  });

/** Lightweight provider summary for the confirm-claim screen (public). */
export const getClaimProvider = createServerFn({ method: "GET" })
  .inputValidator(z.object({ providerId: z.string().uuid() }))
  .handler(async ({ data }) => {
    const p = await db.one(
      `SELECT id, name, practice, specialty, city, state, npi, phone, email, claimed_by, claim_status
         FROM public.providers
        WHERE id = $1`,
      [data.providerId],
    );
    if (!p) throw new Error("Listing not found");
    return p;
  });

/**
 * Public search to help providers find their own listing to claim.
 * Matches across name, practice, NPI, license number, address, city, state.
 */
export const searchClaimableProviders = createServerFn({ method: "POST" })
  .inputValidator(z.object({ q: z.string().trim().min(2).max(120) }))
  .handler(async ({ data }) => {
    const raw = data.q.trim();
    const cols =
      "id, name, practice, specialty, city, state, address, npi, license_number, license_state, claimed_by, listing_status";

    if (/^\d{10}$/.test(raw)) {
      const byNpi = await db.query(
        `SELECT ${cols}
           FROM public.providers
          WHERE npi = $1
          LIMIT 8`,
        [raw],
      );
      if (byNpi.length) return { results: byNpi };
    }

    const tokens = raw.split(/\s+/).filter(Boolean).slice(0, 6);

    // Each token must match (ILIKE) at least one searchable column; tokens are
    // ANDed together. The match value is always a bound param (never
    // concatenated) — `%esc%` is the parameter value, not SQL text.
    const clauses: string[] = [];
    const params: any[] = [];
    for (const tok of tokens) {
      const esc = tok.replace(/[%,()]/g, " ").trim();
      if (!esc) continue;
      params.push(`%${esc}%`);
      const n = params.length;
      clauses.push(
        `(name ILIKE $${n} OR practice ILIKE $${n} OR city ILIKE $${n} OR state ILIKE $${n} OR address ILIKE $${n} OR npi ILIKE $${n} OR license_number ILIKE $${n})`,
      );
    }

    const where = clauses.length ? `WHERE ${clauses.join(" AND ")}` : "";
    const rows = await db.query(
      `SELECT ${cols}
         FROM public.providers
         ${where}
        LIMIT 8`,
      params,
    );
    return { results: rows ?? [] };
  });
